"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

export const GRADE_SCALE: { letter: string; points: number }[] = [
  { letter: "A+", points: 4.0 },
  { letter: "A", points: 4.0 },
  { letter: "A-", points: 3.7 },
  { letter: "B+", points: 3.3 },
  { letter: "B", points: 3.0 },
  { letter: "B-", points: 2.7 },
  { letter: "C+", points: 2.3 },
  { letter: "C", points: 2.0 },
  { letter: "C-", points: 1.7 },
  { letter: "D+", points: 1.3 },
  { letter: "D", points: 1.0 },
  { letter: "D-", points: 0.7 },
  { letter: "F", points: 0.0 },
];

const GRADE_POINTS: Record<string, number> = Object.fromEntries(
  GRADE_SCALE.map((g) => [g.letter, g.points])
);

export type CourseLevel = "regular" | "honors" | "ap-ib";

const LEVEL_BONUS: Record<CourseLevel, number> = {
  regular: 0,
  honors: 0.5,
  "ap-ib": 1.0,
};

export interface CourseRow {
  id: number;
  name: string;
  grade: string;
  level: CourseLevel;
  credits: string;
}

let nextRowId = 0;

function makeRow(): CourseRow {
  nextRowId += 1;
  return {
    id: nextRowId,
    name: "",
    grade: "A",
    level: "regular",
    credits: "1",
  };
}

function weightedPoints(letter: string, level: CourseLevel): number {
  const base = GRADE_POINTS[letter];
  if (base === undefined || base <= 0) {
    return 0;
  }
  return base + LEVEL_BONUS[level];
}

interface GpaTotals {
  credits: number;
  unweighted: number;
  weighted: number;
}

export function computeGpa(rows: CourseRow[]): GpaTotals {
  let credits = 0;
  let unweightedPoints = 0;
  let weightedTotal = 0;
  for (const row of rows) {
    const c = parseNumberInput(row.credits);
    const base = GRADE_POINTS[row.grade];
    if (c !== null && c > 0 && base !== undefined) {
      credits += c;
      unweightedPoints += c * base;
      weightedTotal += c * weightedPoints(row.grade, row.level);
    }
  }
  if (credits <= 0) {
    return { credits: 0, unweighted: 0, weighted: 0 };
  }
  return {
    credits,
    unweighted: unweightedPoints / credits,
    weighted: weightedTotal / credits,
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const HIGH_SCHOOL_GPA_CALCULATOR_STRINGS = {
  regular: "Regular",
  honors: "Honors",
  apIb: "AP / IB",
  yourClasses: "Your Classes",
  classNameOptional: "Class name (optional)",
  classPlaceholder: "Class {number}",
  grade: "Grade",
  level: "Level",
  credits: "Credits",
  removeClassAria: "Remove class {number}",
  remove: "Remove",
  addClass: "Add Class",
  weightedGpa: "Weighted GPA",
  unweightedGpa: "Unweighted GPA",
  totalCredits: "Total credits",
  weightedGradeScaleReference: "Weighted Grade Scale Reference",
  scaleReferenceIntro:
    "The most common weighting adds +0.5 for Honors and +1.0 for AP or IB courses. An F earns 0.0 at every level. Your school may use a different system — check your transcript.",
  letterGrade: "Letter grade",
  honorsPlusHalf: "Honors (+0.5)",
  apIbPlusOne: "AP / IB (+1.0)",
} as const;

export default function HighSchoolGpaCalculatorWidget() {
  const [rows, setRows] = useState<CourseRow[]>(() => [
    makeRow(),
    makeRow(),
    makeRow(),
  ]);

  const strings = useToolUi(HIGH_SCHOOL_GPA_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  const levelOptions: { value: CourseLevel; label: string }[] = [
    { value: "regular", label: strings.regular },
    { value: "honors", label: strings.honors },
    { value: "ap-ib", label: strings.apIb },
  ];

  const totals = computeGpa(rows);

  function updateRow(id: number, patch: Partial<CourseRow>) {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    setRows((prev) => [...prev, makeRow()]);
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }

  return (
    <>
      {/* Course rows */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.yourClasses}
        </h2>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"
              key={row.id}
            >
              <div className="min-w-0 basis-full space-y-1 sm:flex-1 sm:basis-auto">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`course-name-${row.id}`}
                >
                  {strings.classNameOptional}
                </label>
                <input
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`course-name-${row.id}`}
                  onChange={(e) => updateRow(row.id, { name: e.target.value })}
                  placeholder={t(strings.classPlaceholder, {
                    number: index + 1,
                  })}
                  type="text"
                  value={row.name}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`course-grade-${row.id}`}
                >
                  {strings.grade}
                </label>
                <select
                  className="rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`course-grade-${row.id}`}
                  onChange={(e) => updateRow(row.id, { grade: e.target.value })}
                  value={row.grade}
                >
                  {GRADE_SCALE.map((g) => (
                    <option key={g.letter} value={g.letter}>
                      {g.letter}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`course-level-${row.id}`}
                >
                  {strings.level}
                </label>
                <select
                  className="rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`course-level-${row.id}`}
                  onChange={(e) =>
                    updateRow(row.id, {
                      level: e.target.value as CourseLevel,
                    })
                  }
                  value={row.level}
                >
                  {levelOptions.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`course-credits-${row.id}`}
                >
                  {strings.credits}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`course-credits-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { credits: e.target.value })
                  }
                  step="0.5"
                  type="number"
                  value={row.credits}
                />
              </div>
              <button
                aria-label={t(strings.removeClassAria, { number: index + 1 })}
                className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={rows.length <= 1}
                onClick={() => removeRow(row.id)}
                type="button"
              >
                {strings.remove}
              </button>
            </div>
          ))}
        </div>
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={addRow}
          type="button"
        >
          {strings.addClass}
        </button>
      </section>

      {/* Results */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard
          label={strings.weightedGpa}
          value={fmt(totals.weighted, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        />
        <StatCard
          label={strings.unweightedGpa}
          value={fmt(totals.unweighted, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        />
        <StatCard label={strings.totalCredits} value={totals.credits} />
      </div>

      {/* Scale reference */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.weightedGradeScaleReference}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.scaleReferenceIntro}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b">
                <th className="py-2 pr-4 font-medium text-foreground">
                  {strings.letterGrade}
                </th>
                <th className="py-2 pr-4 font-medium text-foreground">
                  {strings.regular}
                </th>
                <th className="py-2 pr-4 font-medium text-foreground">
                  {strings.honorsPlusHalf}
                </th>
                <th className="py-2 font-medium text-foreground">
                  {strings.apIbPlusOne}
                </th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              {GRADE_SCALE.map((g) => (
                <tr className="border-border/50 border-b" key={g.letter}>
                  <td className="py-2 pr-4 font-medium text-foreground">
                    {g.letter}
                  </td>
                  <td className="py-2 pr-4">
                    {fmt(g.points, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="py-2 pr-4">
                    {fmt(weightedPoints(g.letter, "honors"), {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="py-2">
                    {fmt(weightedPoints(g.letter, "ap-ib"), {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
